import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import client from './api/client';
import PageHeader from './components/PageHeader';
import FittingEditModal from './components/FittingEditModal';
import './SeedingPage.css';
import './ContractSeedingPage.css';

function ContractSeedingPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [destinations, setDestinations] = useState([]);
  const [fittings, setFittings] = useState([]);

  const [showAddFittingModal, setShowAddFittingModal] = useState(false);
  const [eftText, setEftText] = useState('');
  const [addingFitting, setAddingFitting] = useState(false);
  const [fittingError, setFittingError] = useState('');
  const [editingFitting, setEditingFitting] = useState(null);

  const checkAuthentication = useCallback(async () => {
    try {
      const response = await client.get('/auth/user/');
      return response.data.authenticated;
    } catch {
      return false;
    }
  }, []);

  const fetchDestinations = useCallback(async () => {
    try {
      const response = await client.get('/seeding/destinations/');
      setDestinations(response.data.destinations || []);
    } catch (err) {
      if (err.response?.status === 401) navigate('/');
    }
  }, [navigate]);

  const fetchFittings = useCallback(async () => {
    try {
      const response = await client.get('/contracts/fittings/');
      setFittings(response.data.fittings || []);
    } catch {
      console.error('Failed to fetch fittings');
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      // In parallel to save a round trip; logged out, the login prompt still shows
      const [authenticated] = await Promise.all([checkAuthentication(), fetchDestinations(), fetchFittings()]);
      setIsAuthenticated(authenticated);
      setLoading(false);
    };
    load();
  }, [checkAuthentication, fetchDestinations, fetchFittings]);

  const handleAddFitting = async (e) => {
    e.preventDefault();
    if (!eftText.trim()) {
      setFittingError('Paste an EFT fitting first');
      return;
    }
    setAddingFitting(true);
    setFittingError('');
    try {
      const response = await client.post('/contracts/fittings/add/', { raw_fitting: eftText.trim() });
      setFittings(prev => [...prev, response.data]);
      setShowAddFittingModal(false);
      setEftText('');
    } catch (err) {
      setFittingError(err.response?.data?.error || 'Failed to add fitting');
    } finally {
      setAddingFitting(false);
    }
  };

  const handleDeleteFitting = async (fittingId, fittingName) => {
    if (!window.confirm(`Delete fitting "${fittingName}"? It will be removed from all destinations.`)) return;
    try {
      await client.delete(`/contracts/fittings/${fittingId}/remove/`);
      setFittings(prev => prev.filter(f => f.id !== fittingId));
    } catch {
      alert('Failed to delete fitting');
    }
  };

  if (!isAuthenticated && !loading) {
    return (
      <div className="page">
        <PageHeader title="Contract Seeding" />
        <div className="page-status">Please log in with your EVE Online character to access contract seeding.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page">
        <PageHeader title="Contract Seeding" />
        <div className="page-status">Loading...</div>
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader title="Contract Seeding">
        <button onClick={() => setShowAddFittingModal(true)} className="btn btn-primary">
          Add fitting
        </button>
      </PageHeader>
      <div className="page-content">
        <div className="contract-page-grid">
          <section className="panel">
            <div className="panel-head">
              <h2>Fittings</h2>
              <span className="panel-sub">{fittings.length} saved</span>
            </div>
            {fittings.length === 0 ? (
              <div className="table-empty">No fittings yet. Use "Add fitting" and paste an EFT fitting.</div>
            ) : (
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Ship</th>
                      <th>Fitting</th>
                      <th className="num">Items</th>
                      <th aria-label="Actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {fittings.map(f => (
                      <tr key={f.id}>
                        <td><b>{f.ship_type_name}</b></td>
                        <td>{f.name}</td>
                        <td className="num">{f.parsed_items.length}</td>
                        <td className="num">
                          <span className="btn-row row-action settings-actions">
                            <button onClick={() => setEditingFitting(f)} className="btn btn-sm">Edit</button>
                            <button onClick={() => handleDeleteFitting(f.id, f.name)} className="btn btn-danger btn-sm">Delete</button>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2>Destinations</h2>
              <span className="panel-sub">Shared with market seeding. Open one to manage its contracts.</span>
            </div>
            {destinations.length === 0 ? (
              <div className="table-empty">No destinations yet. Add one on the <Link to="/seeding">Seeding page</Link>.</div>
            ) : (
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Region</th>
                      <th>System</th>
                    </tr>
                  </thead>
                  <tbody>
                    {destinations.map(dest => (
                      <tr key={dest.id}>
                        <td><Link to={`/contracts/${dest.id}`}>{dest.destination_name}</Link></td>
                        <td>{dest.destination_region}</td>
                        <td>{dest.destination_system}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        {/* Add Fitting Modal */}
        {showAddFittingModal && (
          <div className="modal-overlay">
            <div className="modal fitting-modal">
              <div className="modal-header">
                <h3>Add Ship Fitting</h3>
                <button onClick={() => { setShowAddFittingModal(false); setEftText(''); setFittingError(''); }} className="icon-btn modal-close">×</button>
              </div>
              <form onSubmit={handleAddFitting}>
                <div className="form-group">
                  <label>EFT Fitting:</label>
                  <textarea
                    value={eftText}
                    onChange={e => setEftText(e.target.value)}
                    placeholder={'[Redeemer, My Fitting Name]\nDamage Control II\n...'}
                    rows={16}
                    className="eft-textarea"
                    disabled={addingFitting}
                    autoFocus
                  />
                  <small className="form-hint">
                    Paste an EFT-format fitting. The fitting name is extracted from the header line.
                  </small>
                </div>
                {fittingError && <div className="error-message">{fittingError}</div>}
                <div className="modal-actions">
                  <button type="button" onClick={() => { setShowAddFittingModal(false); setEftText(''); setFittingError(''); }} disabled={addingFitting} className="btn btn-ghost">Cancel</button>
                  <button type="submit" disabled={addingFitting || !eftText.trim()} className="btn btn-primary">
                    {addingFitting ? 'Parsing...' : 'Add Fitting'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {editingFitting && (
          <FittingEditModal
            fitting={editingFitting}
            onClose={() => setEditingFitting(null)}
            onSaved={saved => setFittings(prev => prev.map(f => (f.id === saved.id ? { ...f, ...saved } : f)))}
          />
        )}
      </div>
    </div>
  );
}

export default ContractSeedingPage;
